
import React, { useMemo, useState } from 'react';
import { Task, Submission, Participant } from '../types';
import { CheckSquareIcon, UsersIcon } from './icons';

interface TaskProgressProps {
  tasks: Task[];
  submissions: Submission[];
  participants: Participant[];
  totalParticipantCount: number;
}

const TaskProgress: React.FC<TaskProgressProps> = ({ tasks, submissions, participants, totalParticipantCount }) => {
  const [activeTab, setActiveTab] = useState<'tasks' | 'groups'>('tasks');

  const allParticipantIds = useMemo(() => participants.map(p => p.id), [participants]);

  const taskProgressData = useMemo(() => {
    return tasks.map(task => {
      const taskSubmissions = submissions.filter(s => s.taskId === task.id);
      const submittedParticipantIds = new Set(taskSubmissions.map(s => s.participantId));

      const assignedIds = task.assignedParticipantIds && task.assignedParticipantIds.length > 0
        ? task.assignedParticipantIds
        : allParticipantIds;

      const totalAssigned = assignedIds.length;
      if (totalAssigned === 0) {
        return { ...task, completion: 0, completedCount: taskSubmissions.length, totalAssigned: totalParticipantCount || 0 };
      }

      const completedCount = assignedIds.filter(id => submittedParticipantIds.has(id)).length;
      const completion = Math.round((completedCount / totalAssigned) * 100);

      return { ...task, completion, completedCount, totalAssigned };
    }).sort((a, b) => a.completion - b.completion);
  }, [tasks, submissions, participants, totalParticipantCount, allParticipantIds]);

  const groupProgressData = useMemo(() => {
    const groupMap = new Map<string, typeof taskProgressData>();
    taskProgressData.forEach(task => {
      const groupName = task.customGroup || 'Ungrouped';
      if (!groupMap.has(groupName)) groupMap.set(groupName, []);
      groupMap.get(groupName)!.push(task);
    });

    return Array.from(groupMap.entries()).map(([name, groupTasks]) => {
      const totalCompleted = groupTasks.reduce((sum, t) => sum + t.completedCount, 0);
      const totalAssigned = groupTasks.reduce((sum, t) => sum + t.totalAssigned, 0);
      const overallCompletion = totalAssigned > 0 ? Math.round((totalCompleted / totalAssigned) * 100) : 0;
      return { name, taskCount: groupTasks.length, totalCompleted, totalAssigned, overallCompletion, tasks: groupTasks };
    }).sort((a, b) => a.overallCompletion - b.overallCompletion);
  }, [taskProgressData]);

  // === Overview Card ===
  const OverviewCard = () => (
    <div className="bg-white rounded-xl border border-app-border shadow-sm p-6 flex flex-col min-h-[300px] h-[450px] resize-y overflow-hidden" style={{ resize: 'vertical' }}>
      <h3 className="text-sm font-medium text-text-ink mb-4 border-b border-app-border pb-3 flex-none">Overview</h3>

      <div className="flex-1 overflow-y-auto pr-2 scrollbar-thumb-neutral-200 scrollbar-track-transparent">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-app-bg rounded-xl px-4 py-3 text-center">
            <p className="text-2xl font-bold text-text-ink">{tasks.length}</p>
            <p className="text-[10px] font-bold text-text-muted uppercase mt-1">Total Tasks</p>
          </div>
          <div className="bg-app-bg rounded-xl px-4 py-3 text-center">
            <p className="text-2xl font-bold text-text-ink">{groupProgressData.filter(g => g.name !== 'Ungrouped').length}</p>
            <p className="text-[10px] font-bold text-text-muted uppercase mt-1">Groups</p>
          </div>
          <div className="bg-app-bg rounded-xl px-4 py-3 text-center">
            <p className="text-2xl font-bold text-status-ok">{taskProgressData.filter(t => t.completion === 100).length}</p>
            <p className="text-[10px] font-bold text-text-muted uppercase mt-1">Completed</p>
          </div>
          <div className="bg-app-bg rounded-xl px-4 py-3 text-center">
            <p className="text-2xl font-bold text-text-ink">{taskProgressData.filter(t => t.completion < 100).length}</p>
            <p className="text-[10px] font-bold text-text-muted uppercase mt-1">In Progress</p>
          </div>
        </div>

        {/* Group breakdown */}
        {groupProgressData.filter(g => g.name !== 'Ungrouped').length > 0 && (
          <div>
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Group Breakdown</p>
            <div className="space-y-1">
              {groupProgressData.filter(g => g.name !== 'Ungrouped').map(group => (
                <div key={group.name} className="flex items-center justify-between rounded-xl px-3 py-2 hover:bg-app-bg transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: group.overallCompletion === 100 ? '#6E7A5A' : '#7BBFAF' }}></div>
                    <span className="text-[13px] font-bold text-text-ink truncate">{group.name}</span>
                  </div>
                  <span className={`text-[11px] font-bold uppercase tracking-wider ${group.overallCompletion === 100 ? 'text-status-ok' : 'text-text-muted'}`}>{group.overallCompletion}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Drag Handle */}
      <div className="flex-none flex justify-center pt-3 pb-1 cursor-ns-resize opacity-30 hover:opacity-60 transition-opacity">
        <div className="w-10 h-1 bg-neutral-300 rounded-full"></div>
      </div>
    </div>
  );

  // === Task/Group Progress Card ===
  const ProgressCard = () => (
    <div className="bg-white rounded-xl border border-app-border shadow-sm p-6 flex flex-col min-h-[300px] h-[450px] resize-y overflow-hidden" style={{ resize: 'vertical' }}>
      {/* Tabs */}
      <div className="flex-none flex items-center gap-6 mb-4 border-b border-app-border pb-3">
        <button
          onClick={() => setActiveTab('tasks')}
          className={`text-sm font-bold pb-1 border-b-2 transition-colors ${activeTab === 'tasks' ? 'border-action text-action' : 'border-transparent text-text-faint hover:text-text-muted'}`}
        >
          Task Progress
        </button>
        <button
          onClick={() => setActiveTab('groups')}
          className={`text-sm font-bold pb-1 border-b-2 transition-colors ${activeTab === 'groups' ? 'border-action text-action' : 'border-transparent text-text-faint hover:text-text-muted'}`}
        >
          Group Progress
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pr-2 scrollbar-thumb-neutral-200 scrollbar-track-transparent">
        {activeTab === 'tasks' ? (
          <div className="space-y-5">
            {taskProgressData.length > 0 ? taskProgressData.map(task => (
              <div key={task.id}>
                <div className="flex justify-between items-center mb-1.5">
                  <p className="text-[13px] font-bold text-text-ink truncate" title={task.title || task.prompt}>{task.title || task.prompt}</p>
                  <p className={`text-[11px] font-bold uppercase tracking-wider ${task.completion === 100 ? 'text-status-ok' : 'text-text-muted'}`}>{task.completion}%</p>
                </div>
                <div className="w-full rounded-full h-1 overflow-hidden" style={{ backgroundColor: '#E8E4DF' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${task.completion}%`, backgroundColor: task.completion >= 100 ? '#6E7A5A' : task.completion > 0 ? '#7BBFAF' : 'transparent' }}
                  ></div>
                </div>
                <p className="text-[11px] font-medium text-text-muted opacity-80 uppercase tracking-wider mt-2">{task.completedCount} / {task.totalAssigned} participants submitted</p>
              </div>
            )) : (
              <div className="flex flex-col items-center justify-center py-8 text-center text-text-faint">
                <CheckSquareIcon className="w-10 h-10 mb-2 opacity-30" />
                <p className="text-sm font-medium">No tasks created yet.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {groupProgressData.length > 0 ? groupProgressData.map(group => (
              <div key={group.name}>
                <div className="flex justify-between items-center mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[13px] font-bold text-text-ink truncate">{group.name}</span>
                    <span className="text-[10px] font-bold text-text-faint bg-action/5 px-1.5 py-0.5 rounded-md flex-shrink-0">{group.taskCount} task{group.taskCount !== 1 ? 's' : ''}</span>
                  </div>
                  <p className={`text-[11px] font-bold uppercase tracking-wider flex-shrink-0 ${group.overallCompletion === 100 ? 'text-status-ok' : 'text-text-muted'}`}>{group.overallCompletion}%</p>
                </div>
                <div className="w-full rounded-full h-1 overflow-hidden" style={{ backgroundColor: '#E8E4DF' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${group.overallCompletion}%`, backgroundColor: group.overallCompletion >= 100 ? '#6E7A5A' : group.overallCompletion > 0 ? '#7BBFAF' : 'transparent' }}
                  ></div>
                </div>
                <p className="text-[11px] font-medium text-text-muted opacity-80 uppercase tracking-wider mt-2">{group.totalCompleted} / {group.totalAssigned} total responses</p>
              </div>
            )) : (
              <div className="flex flex-col items-center justify-center py-8 text-center text-text-faint">
                <UsersIcon className="w-10 h-10 mb-2 opacity-30" />
                <p className="text-sm font-medium">No task groups created yet.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Drag Handle */}
      <div className="flex-none flex justify-center pt-3 pb-1 cursor-ns-resize opacity-30 hover:opacity-60 transition-opacity">
        <div className="w-10 h-1 bg-neutral-300 rounded-full"></div>
      </div>
    </div>
  );

  return (
    <>
      <OverviewCard />
      <ProgressCard />
    </>
  );
};

export default TaskProgress;
